import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useAuthProvider } from 'src/context/AuthProvider'
import type {
  IMutation,
  IMutationRecordSelfReportedSourceArgs,
  IQuery,
  SelfReportedSource,
  SelfReportedSourceSurface
} from 'src/types/graphql'
import posthog from 'src/utils/pixels/posthog'

import {
  DETAIL_SOURCES,
  type SelfReportedAudience
} from 'src/components/SelfReportedSource/options'
import {
  hasAnsweredLocally,
  markAnsweredLocally
} from 'src/components/SelfReportedSource/storage'

const RECORD_SELF_REPORTED_SOURCE = gql`
  mutation RecordSelfReportedSource($input: SelfReportedSourceInput!) {
    recordSelfReportedSource(input: $input)
  }
`

/**
 * Its OWN tiny query, deliberately NOT a field on AuthProvider's CURRENT_USER:
 * that query runs on every page, and selecting a field an older server does
 * not know fails the whole thing (every signed-in user would look logged out
 * during a deploy gap). Here a failure only means "ask anyway" — the server's
 * set-once is the real guard.
 */
const SELF_REPORTED_SOURCE_STATUS = gql`
  query SelfReportedSourceStatus {
    currentUser {
      id
      selfReportedSource {
        source
      }
    }
  }
`

/**
 * PostHog values are lowercase by rule (docs/marketing/analytics-reference.md
 * → "Casing rule") — never a raw GraphQL enum. `REFERRAL` → `referral`.
 * HubSpot keeps the enum values: they are its dropdown options.
 */
const toAnalyticsValue = (value: string): string => value.toLowerCase()

/**
 *   pending — deciding (render nothing; SSR + first client render)
 *   hidden  — already answered / admin / nothing to ask
 *   asking  — chips on screen
 *   detail  — answered with a source that offers "¿quién?"; optional input
 *   done    — thanks line
 */
export type SelfReportedStatus =
  | 'pending'
  | 'hidden'
  | 'asking'
  | 'detail'
  | 'done'

type UseSelfReportedSourceParams = {
  surface: SelfReportedSourceSurface
  audience: SelfReportedAudience
  /** Anonymous lead forms only — the contact the visitor just submitted. */
  contact?: { email?: string | null; phone?: string | null }
}

export function useSelfReportedSource({
  surface,
  audience,
  contact
}: UseSelfReportedSourceParams) {
  const { currentUser, loadingCurrentUser } = useAuthProvider()
  const [status, setStatus] = useState<SelfReportedStatus>('pending')
  const [selected, setSelected] = useState<SelfReportedSource | null>(null)
  const shownFiredRef = useRef(false)

  const isSignedIn = Boolean(currentUser)
  const { data, loading, error } = useQuery<Pick<IQuery, 'currentUser'>>(
    SELF_REPORTED_SOURCE_STATUS,
    { skip: !isSignedIn, fetchPolicy: 'cache-first' }
  )
  const [recordMutation] = useMutation<
    Pick<IMutation, 'recordSelfReportedSource'>,
    IMutationRecordSelfReportedSourceArgs
  >(RECORD_SELF_REPORTED_SOURCE)

  // Decide once. Never re-decide after the visitor has interacted — marking
  // the answer locally must not make the widget vanish mid-interaction.
  useEffect(() => {
    if (status !== 'pending') return
    if (loadingCurrentUser || (isSignedIn && loading)) return
    // Admins publish listings / create checkouts on hosts' behalf — their
    // answer would be about SpotMe staff, not the customer.
    if (currentUser?.isAdmin) {
      setStatus('hidden')
      return
    }
    const answeredOnServer = Boolean(
      !error && data?.currentUser?.selfReportedSource?.source
    )
    setStatus(answeredOnServer || hasAnsweredLocally() ? 'hidden' : 'asking')
  }, [
    status,
    loadingCurrentUser,
    isSignedIn,
    loading,
    error,
    data,
    currentUser?.isAdmin
  ])

  // Impression — the denominator for the answer rate.
  useEffect(() => {
    if (status !== 'asking' || shownFiredRef.current) return
    shownFiredRef.current = true
    posthog.event('self_reported_source_shown', {
      surface: toAnalyticsValue(surface),
      audience
    })
  }, [status, surface, audience])

  const send = useCallback(
    (source: SelfReportedSource, detail?: string) => {
      recordMutation({
        variables: {
          input: {
            source,
            surface,
            detail: detail ?? null,
            // The server ignores these when signed in (token identity only).
            email: isSignedIn ? null : contact?.email?.trim() || null,
            phone: isSignedIn ? null : contact?.phone?.trim() || null
          }
        }
      }).catch(() => {
        // Survey answers are best-effort; PostHog already has the answer.
      })
    },
    [recordMutation, surface, isSignedIn, contact?.email, contact?.phone]
  )

  const choose = useCallback(
    (source: SelfReportedSource) => {
      if (status !== 'asking') return
      setSelected(source)
      setStatus(DETAIL_SOURCES.has(source) ? 'detail' : 'done')
      markAnsweredLocally(source)
      posthog.event('self_reported_source_answered', {
        source: toAnalyticsValue(source),
        surface: toAnalyticsValue(surface),
        audience,
        $set_once: {
          self_reported_source: toAnalyticsValue(source),
          self_reported_source_surface: toAnalyticsValue(surface)
        }
      })
      send(source)
    },
    [status, surface, audience, send]
  )

  const submitDetail = useCallback(
    (detail: string) => {
      if (status !== 'detail' || !selected) return
      setStatus('done')
      const clean = detail.trim()
      if (!clean) return
      // Free text never goes to PostHog (it can be a person's name).
      posthog.event('self_reported_source_detail_added', {
        source: toAnalyticsValue(selected),
        surface: toAnalyticsValue(surface),
        audience
      })
      send(selected, clean)
    },
    [status, selected, surface, audience, send]
  )

  return { status, selected, choose, submitDetail }
}
